// GL11 하단(송장 이력) 조회 — 2.0 GET /api/v1/global/invoice-monitorings/histories (LOGISPRD-9600)
// 마스터 선택 행(화주+송장번호)을 구독해 서버 조회한다 (MA03 ITEM 패턴). 조회 전용 표.
// 엑셀 다운로드 2종 — 2.0 GET /excel (HEAD 27컬럼) · GET /histories/excel (ITEM 7컬럼, 이력 그리드
// 전용 파리티 — 1.0 GL11_ITEM TOOL_EXP=1). 둘 다 xlsx 직접 응답 + EXPORT 감사 (LOGISPRD-9602)
// 순수 변환(행 매핑/query string/파일명 파싱)은 mapping.ts 에 분리 — request 비의존 모듈로 테스트한다.
import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import request, { getExcel } from '@/utils/request'
import { GL11_URL_LENGTH_THRESHOLD } from '../components/consts'
import { buildExcelQuery, buildHistoriesExcelQuery, parseExcelFileName, toHistoryRow, toMonitoringQueryError } from './mapping'
import type { GlobalMonitoringParams, HistoryItem, MonitoringHistoryRow } from './mapping'

export type { MonitoringHistoryRow } from './mapping'

export const monitoringHistoriesQueryKeys = {
  all: ['getMonitoringHistories'] as const,
  detail: (shipperCode: string, invoiceNo: string, closingDate?: string) =>
    [...monitoringHistoriesQueryKeys.all, shipperCode, invoiceNo, closingDate] as const,
}

export const fetchMonitoringHistories = async (shipperCode: string, invoiceNo: string, closingDate?: string): Promise<MonitoringHistoryRow[]> => {
  try {
    const res = await request.get<ApiResponseV2<{ items: HistoryItem[] }>, { shipperCode: string; invoiceNo: string; closingDate?: string }>(
      '/api/v1/global/invoice-monitorings/histories',
      { shipperCode, invoiceNo, closingDate },
    )
    return (res.data?.items ?? []).map(toHistoryRow)
  } catch (error) {
    throw toMonitoringQueryError(error)
  }
}

export const useGetMonitoringHistories = (
  shipperCode: string,
  invoiceNo: string,
  closingDate?: string,
): UseQueryResult<MonitoringHistoryRow[], Error> =>
  useQuery({
    queryKey: monitoringHistoriesQueryKeys.detail(shipperCode, invoiceNo, closingDate),
    queryFn: () => fetchMonitoringHistories(shipperCode, invoiceNo, closingDate),
    enabled: !!shipperCode && !!invoiceNo,
    // 실패 문구는 GL11 화면의 usePboQueryErrorToast 가 한 번만 알린다. 전역 QueryCache 알럿은 끈다.
    meta: { noErrorMessage: true },
  })

// 공통 xlsx 다운로드 — blob 응답을 Content-Disposition 파일명으로 저장. 사유 모달 통과 후 호출.
const downloadExcelFile = async (url: string, fallbackFileName: string, config?: Parameters<typeof getExcel>[1]) => {
  let res
  try {
    res = await getExcel(url, config)
  } catch (e) {
    // responseType blob 이라 400(대량 가드: 화주·31일·1만행, 이력 필수 파라미터) 의 meta.message 가 Blob 로 옴 — JSON 복원해 사유 노출
    const resp = (e as { response?: { data?: unknown } })?.response
    if (resp?.data instanceof Blob) {
      try {
        resp.data = JSON.parse(await (resp.data as Blob).text())
      } catch {
        /* JSON 아니면 상태코드 폴백 유지 */
      }
    }
    throw e
  }
  const fileName = parseExcelFileName(String(res.headers?.['content-disposition'] ?? ''), fallbackFileName)
  const a = document.createElement('a')
  a.href = URL.createObjectURL(res.data)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
}

// HEAD 엑셀 다운로드 — 1.0 파리티(필터 전체) + 서버 xlsx(EXPORT 감사, apiName=GL11_HEAD).
// detailedReason(사유코드_상세사유) 은 BE 가 EXPORT 감사로그 remark 에 남긴다 (LOGISPRD-9602)
export const downloadMonitoringExcel = async (params: GlobalMonitoringParams, detailedReason?: string) => {
  const legacyUrl = `/api/v1/global/invoice-monitorings/excel${buildExcelQuery(params, detailedReason)}`
  if (legacyUrl.length <= GL11_URL_LENGTH_THRESHOLD) {
    return downloadExcelFile(legacyUrl, '모니터링CS.xlsx')
  }

  const body = {
    ...(params.shipperCode ? { shipperCode: params.shipperCode } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.closingDateFrom ? { closingDateFrom: params.closingDateFrom } : {}),
    ...(params.closingDateTo ? { closingDateTo: params.closingDateTo } : {}),
    ...(params.invoiceNos?.length ? { invoiceNos: params.invoiceNos } : {}),
    ...(params.shipperOrderNos?.length ? { shipperOrderNos: params.shipperOrderNos } : {}),
  }
  const reasonQuery = detailedReason ? `?detailedReason=${encodeURIComponent(detailedReason)}` : ''
  try {
    return await downloadExcelFile(`/api/v1/global/invoice-monitorings/excel/search${reasonQuery}`, '모니터링CS.xlsx', {
      method: 'post',
      data: body,
    })
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status !== 404 && status !== 405) throw error
    return downloadExcelFile(legacyUrl, '모니터링CS.xlsx')
  }
}

// ITEM(송장 이력) 엑셀 다운로드 — 이력 그리드 전용 (1.0 GL11_ITEM TOOL_EXP=1 파리티, LOGISPRD-9602).
// /histories 조회와 동일 컨텍스트(화주 + 선택 HEAD 행 송장번호)로 서버 xlsx(EXPORT 감사, apiName=GL11_ITEM)
export const downloadMonitoringHistoriesExcel = (shipperCode: string, invoiceNo: string, closingDate?: string, detailedReason?: string) =>
  downloadExcelFile(
    `/api/v1/global/invoice-monitorings/histories/excel${buildHistoriesExcelQuery(shipperCode, invoiceNo, closingDate, detailedReason)}`,
    '모니터링CS_이력.xlsx',
  )
